import React from 'react';
import { X, Shield, CheckCircle, AlertCircle, Lock, Key } from 'lucide-react';

interface PasswordSecurityInfoProps {
  onClose: () => void;
}

export const PasswordSecurityInfo: React.FC<PasswordSecurityInfoProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Lock className="w-6 h-6 text-green-600" />
            <span>Passwort-Sicherheit</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Passwortschutz aktiviert</h3>
                <p className="text-sm text-green-700 mt-1">
                  InstallTrack Pro prüft Passwörter gegen bekannte Datenlecks, um Ihre Sicherheit zu gewährleisten.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Passwort-Richtlinien</h3>
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Mindestlänge: 8 Zeichen</p>
                  <p className="text-sm text-gray-500">Längere Passwörter sind schwieriger zu knacken</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Komplexität</p>
                  <p className="text-sm text-gray-500">Muss Groß- und Kleinbuchstaben sowie Zahlen enthalten</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Prüfung gegen bekannte Datenlecks</p>
                  <p className="text-sm text-gray-500">Passwörter werden gegen die HaveIBeenPwned.org-Datenbank geprüft</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Wie funktioniert der Schutz vor kompromittierten Passwörtern?</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 mb-4">
                Wenn Sie ein Passwort erstellen oder ändern, wird es gegen die Datenbank von HaveIBeenPwned.org geprüft. 
                Diese Datenbank enthält über 613 Millionen kompromittierte Passwörter aus bekannten Datenlecks.
              </p>
              <p className="text-sm text-gray-700 mb-4">
                <strong>Wichtig:</strong> Ihr Passwort wird niemals im Klartext übertragen. Stattdessen wird ein sicherer Hash-Wert 
                verwendet, um zu prüfen, ob Ihr Passwort in einem bekannten Datenleck enthalten ist.
              </p>
              <p className="text-sm text-gray-700">
                Wenn Ihr Passwort als kompromittiert erkannt wird, werden Sie aufgefordert, ein anderes zu wählen. 
                Dies schützt Sie vor potenziellen Angriffen, bei denen Angreifer bekannte Passwörter aus Datenlecks verwenden.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tipps für sichere Passwörter</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Shield className="h-5 w-5 text-green-500 mr-2" />
                  <h4 className="font-medium text-gray-900">Verwenden Sie einen Passwort-Manager</h4>
                </div>
                <p className="text-sm text-gray-500">
                  Passwort-Manager helfen Ihnen, einzigartige und komplexe Passwörter für jedes Konto zu erstellen und zu speichern.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Key className="h-5 w-5 text-green-500 mr-2" />
                  <h4 className="font-medium text-gray-900">Verwenden Sie Passphrasen</h4>
                </div>
                <p className="text-sm text-gray-500">
                  Lange Passphrasen aus mehreren Wörtern sind leichter zu merken und schwerer zu knacken als kurze, komplexe Passwörter.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                  <h4 className="font-medium text-gray-900">Vermeiden Sie persönliche Informationen</h4>
                </div>
                <p className="text-sm text-gray-500">
                  Verwenden Sie keine leicht zu erratenden Informationen wie Geburtstage, Namen von Haustieren oder Familienmitgliedern.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                  <h4 className="font-medium text-gray-900">Verwenden Sie unterschiedliche Passwörter</h4>
                </div>
                <p className="text-sm text-gray-500">
                  Verwenden Sie für jedes Konto ein einzigartiges Passwort, um zu verhindern, dass ein Datenleck mehrere Ihrer Konten gefährdet.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};